import React, { createContext, useContext, useEffect, useState } from "react";
import { getUserDetails } from "../api/authService";

interface AuthUser {
  user_id : number;
  user_username : string;
  email: string;
  role: string;
  vertical_id: number;
  vertical_key: string;
  vertical_name: string;
}

interface UserDetails {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  
}

interface AuthContextType {
  user: AuthUser | null;
  userDetails: UserDetails | null;
  token: string | null;
    isAuthReady: boolean;   // ✅ ADD
  login: (data: AuthUser, token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
const [isAuthReady, setIsAuthReady] = useState(false);

  // 🔄 Restore session
  useEffect(() => {
  const storedToken = localStorage.getItem("user_token");
  const storedUser = localStorage.getItem("user_data");

  if (storedToken && storedUser) {
    setToken(storedToken);
    const parsedUser = JSON.parse(storedUser);
    setUser(parsedUser);

    setUserDetails({
      username: parsedUser.user_username,
      email: parsedUser.email,
      first_name: parsedUser.email.split("@")[0],
      last_name: "",
      role: parsedUser.role,
    });
  }

  // ✅ AUTH RESTORE COMPLETE
  setIsAuthReady(true);
}, []);


  // 🔍 Optional backend enrichment
  useEffect(() => {
    if (!token) return;

    getUserDetails()
      .then((res) => {
        const data = res.data?.data?.[0];
        if (!data) return; // ✅ backend empty → ignore

        setUserDetails({
          username: data.user_id.username,
          email: data.user_id.email,
          first_name: data.user_id.first_name,
          last_name: data.user_id.last_name,
          role: data.role,
        });
      })
      .catch((err) => {
        console.warn("User details API failed, using fallback", err);
      });
  }, [token]);

  const login = (userData: AuthUser, authToken: string) => {
    localStorage.setItem("user_token", authToken);
    localStorage.setItem("user_data", JSON.stringify(userData));

    setToken(authToken);
    setUser(userData);

    // ✅ Immediate fallback userDetails
    setUserDetails({
      username: userData.email.split("@")[0],
      email: userData.email,
      first_name: userData.email.split("@")[0],
      last_name: "",
      role: userData.role,
    });
  };

  const logout = () => {
    localStorage.clear();
    setUser(null);
    setToken(null);
    setUserDetails(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, userDetails, token,isAuthReady, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
